/*
  Whether this is, in all likelihood, the very phone that would scan the qr
  code. Only that phone is excluded from the cross-device options: any other
  screen (desktop, tablet, touch laptop) can legitimately display a qr code
  and have it scanned with a phone.

  Proving "desktop" is a losing game — pc with a touchscreen, ipad announcing
  itself as a mac, in-app webview laid out wide. "Phone" is the closed
  category every signal agrees on, and the two layers cover each other's
  blind spot:
  - userAgentData.mobile is the browser's own answer (chromium, including the
    android webview, where the partner iframe width would lie to a media
    query);
  - the physical screen — which an iframe cannot fool — combined with the
    device pointer type answers for the browsers without client hints
    (safari, firefox). The pointer guard keeps an anti-fingerprinting
    shrunken screen from passing as a phone.
  A wrong guess must lean towards offering the option, never removing it.
*/
export function isProbablyPhone(): boolean {
  const uaData = (navigator as NavigatorWithUserAgentData).userAgentData;

  if (uaData != null) {
    return uaData.mobile;
  }

  const smallestScreenSideInPx = Math.min(window.screen.width, window.screen.height);

  return smallestScreenSideInPx < 600 && window.matchMedia?.('(pointer: coarse)').matches === true;
}

/*
  Whether this is, in all likelihood, a touch device — phone OR tablet — as
  opposed to a computer driven with a mouse. The browser's own answer first
  (client hints say mobile for phones, not for android tablets), then the
  primary pointer: coarse on every phone and tablet (an ipad announcing
  itself as a mac included), fine on a computer — a touch laptop keeps a fine
  primary pointer and counts as a computer, which is the safe side here.
*/
export function isProbablyTouchDevice(): boolean {
  const uaData = (navigator as NavigatorWithUserAgentData).userAgentData;

  if (uaData?.mobile === true) {
    return true;
  }

  return window.matchMedia?.('(pointer: coarse)').matches === true;
}

type NavigatorWithUserAgentData = Navigator & { userAgentData?: { mobile: boolean } };
